import React, { useState, useEffect } from 'react';
import { Lock, Download, Mail, Phone, X, Pencil, Send, Pause, Play, Trash2, CalendarOff, CheckCircle } from 'lucide-react';
import { api } from '../api';
import ImportCSV from '../components/ImportCSV';
import PhoneInput from '../components/PhoneInput';
import { useConfirm } from '../components/ConfirmModal';

const API_URL = 'https://qbdp-backend-production.up.railway.app/api';
const card = { background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', padding: '20px 24px' };

function StatusBadge({ statut, enConge }) {
  if (enConge) return <span style={{ background: '#fef3c7', color: '#d97706', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>En congé</span>;
  const map = {
    actif:   { bg: 'var(--green-light)', color: 'var(--green)' },
    inactif: { bg: '#fee2e2', color: '#dc2626' },
  };
  const s = map[statut] || map.inactif;
  return <span style={{ background: s.bg, color: s.color, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{statut}</span>;
}

const TYPE_LABELS = { conge: 'Congé', maladie: 'Maladie', formation: 'Formation', autre: 'Autre' };

function AbsencesModal({ employe, onClose }) {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date_debut: '', date_fin: '', type: 'conge', commentaire: '' });
  const [saving, setSaving] = useState(false);
  const { showConfirm, confirmModal } = useConfirm();

  useEffect(() => {
    api.getAbsences(employe.id)
      .then(setAbsences)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [employe.id]);

  const handleAdd = async () => {
    if (!form.date_debut || !form.date_fin) return;
    setSaving(true);
    try {
      const abs = await api.addAbsence(employe.id, form);
      setAbsences(prev => [abs, ...prev]);
      setForm({ date_debut: '', date_fin: '', type: 'conge', commentaire: '' });
    } catch (err) { alert(err.message); }
    setSaving(false);
  };

  const handleDelete = async (absId, type) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CETTE ABSENCE',
      subtitle: TYPE_LABELS[type] || type,
      body: 'Cette action est irréversible. L\'absence sera définitivement supprimée.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    try {
      await api.deleteAbsence(employe.id, absId);
      setAbsences(prev => prev.filter(a => a.id !== absId));
    } catch (err) { alert(err.message); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const isActive = (a) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const debut = new Date(a.date_debut); const fin = new Date(a.date_fin);
    return debut <= today && today <= fin;
  };

  const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', width: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Absences — {employe.nom}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6b7280" /></button>
        </div>

        {/* Formulaire ajout */}
        <div style={{ background: '#f9fafb', borderRadius: 10, padding: 14, marginBottom: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Nouvelle absence</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Du</div><input type="date" style={inp} value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} /></div>
            <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Au</div><input type="date" style={inp} value={form.date_fin} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} /></div>
            <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Type</div>
              <select style={inp} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>Commentaire</div>
            <input style={inp} placeholder="Optionnel" value={form.commentaire} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))} />
          </div>
          <button onClick={handleAdd} disabled={saving || !form.date_debut || !form.date_fin}
            style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            + Ajouter
          </button>
        </div>

        {/* Liste */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray)', fontSize: 13 }}>Chargement…</div>
        ) : absences.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--gray)', fontSize: 13 }}>Aucune absence enregistrée</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {absences.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isActive(a) ? '#fef3c7' : '#f9fafb', border: `1px solid ${isActive(a) ? '#fde68a' : 'var(--border)'}`, borderRadius: 8, padding: '10px 14px' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>
                    {fmt(a.date_debut)} → {fmt(a.date_fin)}
                    {isActive(a) && <span style={{ marginLeft: 8, fontSize: 10, background: '#d97706', color: 'white', borderRadius: 6, padding: '1px 6px' }}>En cours</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                    {TYPE_LABELS[a.type] || a.type}{a.commentaire ? ` — ${a.commentaire}` : ''}
                  </div>
                </div>
                <button onClick={() => handleDelete(a.id, a.type)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}><Trash2 size={14} color="#dc2626" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {confirmModal}
    </div>
  );
}

function splitNom(nom) {
  if (!nom) return { prenom: '', nom_famille: '' };
  const i = nom.indexOf(' ');
  if (i === -1) return { prenom: '', nom_famille: nom };
  return { nom_famille: nom.slice(0, i), prenom: nom.slice(i + 1) };
}

function F({ label, children }) {
  return <div><div style={{ fontSize: 11, fontWeight: 500, color: '#374151' }}>{label}</div>{children}</div>;
}

function Modal({ onClose, onSave, initial, onGererAcces }) {
  const split = splitNom(initial?.nom);
  const [form, setForm] = useState(initial ? {
    ...initial, prenom: split.prenom, nom_famille: split.nom_famille,
  } : { prenom: '', nom_famille: '', matricule: '', service: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', statut: 'actif', date_naissance: '' });
  const [services, setServices] = useState([]);
  useEffect(() => {
    import('../api').then(m => {
      m.api.getServices().then(setServices).catch(() => {});
    });
  }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', marginTop: 4, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 500, color: '#374151' };

  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [showVilleSuggestions, setShowVilleSuggestions] = useState(false);

  // Uppercase la ville au chargement initial
  useEffect(() => { if (form.ville) set('ville', form.ville.toUpperCase()); }, []);

  const fmtCp = (cp) => cp.length > 2 ? cp.slice(0, 2) + ' ' + cp.slice(2) : cp;

  const handleCodePostal = (e) => {
    const d = e.target.value.replace(/\D/g, '').slice(0, 5);
    set('code_postal', d.length > 2 ? d.slice(0, 2) + ' ' + d.slice(2) : d);
    if (d.length === 5) {
      fetch(`https://geo.api.gouv.fr/communes?codePostal=${d}&fields=nom&limit=5&boost=population`)
        .then(r => r.json())
        .then(data => { if (data.length > 0) set('ville', data[0].nom.toUpperCase()); })
        .catch(() => {});
    }
  };

  const handleVille = (e) => {
    const val = e.target.value.toUpperCase();
    set('ville', val);
    if (val.length >= 2) {
      fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(val)}&fields=nom,codesPostaux&limit=6&boost=population`)
        .then(r => r.json())
        .then(data => {
          const sugg = data.map(c => ({ nom: c.nom.toUpperCase(), cp: c.codesPostaux[0] || '' }));
          setVilleSuggestions(sugg);
          setShowVilleSuggestions(sugg.length > 0);
          if (sugg.length > 0) set('code_postal', fmtCp(sugg[0].cp));
        })
        .catch(() => {});
    } else {
      setVilleSuggestions([]);
      setShowVilleSuggestions(false);
    }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', width: 600, maxHeight: '95vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14 }}>{initial ? 'Modifier' : 'Ajouter'} un employé</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Prénom | Nom | Matricule | Service | Statut */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10 }}>
            <F label="Nom"><input style={inp} placeholder="DUPONT" value={form.nom_famille} onChange={e => set('nom_famille', e.target.value)} /></F>
            <F label="Prénom"><input style={inp} placeholder="Olivier" value={form.prenom} onChange={e => set('prenom', e.target.value)} /></F>
            <F label="Matricule"><input style={inp} placeholder="EMP-001" value={form.matricule} onChange={e => set('matricule', e.target.value)} /></F>
            <F label="Service">
              <select style={inp} value={form.service || ''} onChange={e => set('service', e.target.value)}>
                <option value="">-- Service --</option>
                {services.map(s => <option key={s.id} value={s.nom}>{s.nom}</option>)}
              </select>
            </F>
            <F label="Statut">
              <select style={inp} value={form.statut} onChange={e => set('statut', e.target.value)}>
                <option value="actif">Actif</option>
                <option value="inactif">Inactif</option>
              </select>
            </F>
          </div>
          {/* Contact + Date de naissance */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <F label="Email"><input style={inp} type="email" placeholder="prenom.nom@entreprise.fr" value={form.email} onChange={e => set('email', e.target.value)} /></F>
            <F label="Téléphone"><PhoneInput value={form.telephone} onChange={v => set('telephone', v)} /></F>
            <F label="Date de naissance"><input style={inp} type="date" value={form.date_naissance ? form.date_naissance.slice(0,10) : ''} onChange={e => set('date_naissance', e.target.value)} /></F>
          </div>
          {/* Adresse */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 100px 1fr', gap: 10 }}>
            <F label="Adresse"><input style={inp} placeholder="12 rue de la Paix" value={form.adresse} onChange={e => set('adresse', e.target.value)} /></F>
            <F label="Code postal"><input style={inp} placeholder="75001" value={form.code_postal} onChange={handleCodePostal} /></F>
            <F label="Ville">
              <div style={{ position: 'relative' }}>
                <input style={inp} placeholder="PARIS" value={form.ville}
                  onChange={handleVille}
                  onBlur={() => setTimeout(() => setShowVilleSuggestions(false), 150)} />
                {showVilleSuggestions && villeSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 300, overflow: 'hidden', marginTop: 2 }}>
                    {villeSuggestions.map((s, i) => (
                      <div key={i}
                        onMouseDown={() => { set('ville', s.nom); set('code_postal', fmtCp(s.cp)); setShowVilleSuggestions(false); }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: i < villeSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <span>{s.nom}</span>
                        <span style={{ color: '#9ca3af', fontSize: 11, marginLeft: 8 }}>{s.cp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </F>
          </div>
        </div>
        {initial?.id && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button onClick={() => { onClose(); onGererAcces(initial); }} style={{ width: '100%', background: '#f0f5ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px', fontSize: 13, color: '#1a56db', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Lock size={14} /> Gérer les accès & zones
            </button>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'white', cursor: 'pointer' }}>Annuler</button>
          <button onClick={() => onSave({ ...form, nom: [form.nom_famille, form.prenom].filter(Boolean).join(' ') })} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--blue)', color: 'white', fontWeight: 500, cursor: 'pointer' }}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function SelectionBar({ selected, onClear, onSendEmail, onSendSms, onSendBoth }) {
  const emailCount = selected.filter(s => s.email).length;
  const smsCount   = selected.filter(s => s.telephone).length;
  if (!selected.length) return null;
  const btn = (onClick, bg, label) => (
    <button onClick={onClick} style={{ background: bg, border: 'none', borderRadius: 8, padding: '6px 14px', color: 'white', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
      {label}
    </button>
  );
  return (
    <div style={{ background: '#1a1a2e', color: 'white', borderRadius: 12, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, marginRight: 4 }}>
        {selected.length} sélectionné{selected.length > 1 ? 's' : ''}
        {emailCount > 0 && ` · ${emailCount} email`}
        {smsCount > 0   && ` · ${smsCount} SMS`}
      </span>
      {emailCount > 0 && btn(onSendEmail, '#1e3a8a', 'Envoyer mail')}
      {smsCount   > 0 && btn(onSendSms,   '#5b8dd9', 'Envoyer SMS')}
      {emailCount > 0 && smsCount > 0 && btn(onSendBoth, '#7c3aed', 'Email + SMS')}
      <button onClick={onClear} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '6px 12px', color: 'white', fontSize: 12, cursor: 'pointer' }}>
        Effacer
      </button>
    </div>
  );
}

function ModalEnvoi({ selected, defaultType, onClose, onSent }) {
  const [type, setType]           = useState(defaultType || 'email');
  const [subject, setSubject]     = useState('');
  const [message, setMessage]     = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending]     = useState(false);
  const [result, setResult]       = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setAttachment({ name: file.name, data: base64, type: file.type });
    };
    reader.readAsDataURL(file);
  };

  const recipients = selected.map(s => ({ nom: s.nom, email: s.email, telephone: s.telephone }));
  const emailCount = recipients.filter(r => r.email).length;
  const smsCount   = recipients.filter(r => r.telephone).length;

  const openSmsApp = () => {
    const phones = recipients.filter(r => r.telephone).map(r => r.telephone).join(',');
    const body   = encodeURIComponent(message);
    window.open(`sms:${phones}${/iPhone|iPad|Mac/i.test(navigator.userAgent) ? '&' : '?'}body=${body}`);
    if (onSent) onSent();
    setResult({ smsDone: true, count: recipients.filter(r => r.telephone).length });
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    if (type === 'sms') { openSmsApp(); return; }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/campaigns/send-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients, subject, message, type: 'email', attachment }),
      }).then(r => r.json());
      if (type === 'both' && smsCount > 0) {
        const phones = recipients.filter(r => r.telephone).map(r => r.telephone).join(',');
        const body   = encodeURIComponent(message);
        window.open(`sms:${phones}${/iPhone|iPad|Mac/i.test(navigator.userAgent) ? '&' : '?'}body=${body}`);
      }
      setResult(r);
      if (onSent) onSent();
    } catch (e) { setResult({ error: e.message }); }
    setSending(false);
  };

  const inp = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a2e' }}>Envoyer un message</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>×</button>
        </div>

        {result ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {result.error
              ? <div style={{ color: '#dc2626', fontWeight: 600 }}>Erreur : {result.error}</div>
              : <>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><CheckCircle size={36} color="#15803d" /></div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#15803d' }}>
                    {result.smsDone ? `App SMS ouverte — ${result.count} numéro(s) pré-rempli(s)` : 'Email envoyé !'}
                  </div>
                  {result.emailSent > 0 && <p style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{result.emailSent} email{result.emailSent > 1 ? 's' : ''} envoyé{result.emailSent > 1 ? 's' : ''}</p>}
                  {result.emailFailed > 0 && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{result.emailFailed} échec</p>}
                  <button onClick={onClose} style={{ marginTop: 20, background: '#1e3a8a', color: 'white', border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>Fermer</button>
                </>}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { v: 'email', label: `Email (${emailCount})` },
                { v: 'sms',   label: `SMS (${smsCount})`     },
                { v: 'both',  label: `Les deux`               },
              ].map(opt => (
                <button key={opt.v} onClick={() => setType(opt.v)}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: `2px solid ${type === opt.v ? '#1e3a8a' : '#e5e7eb'}`,
                    background: type === opt.v ? '#eff6ff' : 'white', color: type === opt.v ? '#1e3a8a' : '#6b7280',
                    fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#374151' }}>
              {selected.length} destinataire{selected.length > 1 ? 's' : ''}
              {' — '}{type === 'email' ? `${emailCount} email(s)` : type === 'sms' ? `${smsCount} SMS` : `${emailCount} email(s) + ${smsCount} SMS`}
            </div>
            {(type === 'email' || type === 'both') && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Objet</label>
                <input style={inp} placeholder="Objet de l'email" value={subject} onChange={e => setSubject(e.target.value)} />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message</label>
              <textarea style={{ ...inp, minHeight: 120, resize: 'vertical' }} placeholder="Votre message..." value={message} onChange={e => setMessage(e.target.value)} />
            </div>
            {(type === 'email' || type === 'both') && (
              <div style={{ marginBottom: 16 }}>
                {attachment ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                    <span style={{ fontSize: 16 }}>📎</span>
                    <span style={{ fontSize: 13, color: '#0369a1', fontWeight: 600, flex: 1 }}>{attachment.name}</span>
                    <button onClick={() => setAttachment(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 16, lineHeight: 1 }}>×</button>
                  </div>
                ) : (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 600, background: 'white' }}>
                    <span>📎</span> Joindre un fichier
                    <input type="file" style={{ display: 'none' }} onChange={handleFile} />
                  </label>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={handleSend} disabled={sending || !message.trim()}
                style={{ flex: 2, padding: '11px 0', borderRadius: 10, border: 'none', background: '#1e3a8a', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 13, opacity: (!message.trim() || sending) ? 0.6 : 1 }}>
                {sending ? 'Envoi...' : type === 'sms' ? 'Ouvrir SMS' : 'Envoyer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Employes({ filterService, employees: empFromApp, setEmployees: setEmpFromApp, services, onGererAcces }) {
  const [localEmployees, setLocalEmployees] = useState([]);
  const [search, setSearch]             = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [editEmploye, setEditEmploye]   = useState(null);
  const [loading, setLoading]           = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [serviceTemp, setServiceTemp]   = useState('');
  const [selected, setSelected]         = useState([]);
  const [modalEnvoi, setModalEnvoi]     = useState(null);
  const [sortCol, setSortCol]           = useState('nom');
  const [sortDir, setSortDir]           = useState('asc');
  const [showImport, setShowImport]     = useState(false);
  const [toast, setToast]               = useState(null);
  const [absencesEmploye, setAbsencesEmploye] = useState(null);
  const { showConfirm, confirmModal } = useConfirm();

  const allEmployees    = empFromApp !== undefined ? empFromApp : localEmployees;
  const setAllEmployees = setEmpFromApp !== undefined ? setEmpFromApp : setLocalEmployees;

  const load = () => {
    api.getEmployes().then(setAllEmployees).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (empFromApp === undefined) {
      load();
    } else {
      setLoading(false);
    }
  }, []);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const arrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const filtered = allEmployees.filter(e => {
    const matchSearch = e.nom.toLowerCase().includes(search.toLowerCase()) ||
      (e.service || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.matricule || '').toLowerCase().includes(search.toLowerCase());
    const matchService = !filterService || e.service === filterService;
    return matchSearch && matchService;
  });

  const sorted = [...filtered].sort((a, b) => {
    const valA = (a[sortCol] || '').toString().toLowerCase();
    const valB = (b[sortCol] || '').toString().toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const allSelected = sorted.length > 0 && sorted.every(e => selected.find(s => s.id === e.id));

  const toggleSelect = (emp) => {
    setSelected(prev =>
      prev.find(s => s.id === emp.id) ? prev.filter(s => s.id !== emp.id) : [...prev, emp]
    );
  };
  const toggleAll = () => setSelected(allSelected ? [] : sorted);

  const handleAdd = async (form) => {
    if (!form.prenom && !form.nom_famille) return;
    try {
      const nouveau = await api.addEmploye(form);
      setAllEmployees(prev => [nouveau, ...prev]);
      setShowModal(false);
    } catch (err) { alert(err.message); }
  };

  const handleEdit = async (form) => {
    try {
      const updated = await api.updateEmploye(editEmploye.id, form);
      setAllEmployees(prev => prev.map(e => e.id === editEmploye.id ? { ...e, ...updated } : e));
      setEditEmploye(null);
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (id, nom) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CET EMPLOYÉ',
      subtitle: nom,
      body: 'Cette action est irréversible. L\'employé et tous ses accès seront définitivement supprimés.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    try {
      await api.deleteEmploye(id);
      setAllEmployees(prev => prev.filter(e => e.id !== id));
    } catch (err) { alert(err.message); }
  };

  const toggleStatut = async (e) => {
    const newStatut = e.statut === 'actif' ? 'inactif' : 'actif';
    try {
      const updated = await api.updateEmploye(e.id, { statut: newStatut });
      setAllEmployees(prev => prev.map(emp => emp.id === e.id ? { ...emp, ...updated } : emp));
    } catch (err) { alert(err.message); }
  };

  const handleServiceEdit = (e) => { setEditingService(e.id); setServiceTemp(e.denomination || e.service || ''); };

  const handleServiceSave = async (id) => {
    try {
      await api.updateEmploye(id, { denomination: serviceTemp });
      setAllEmployees(prev => prev.map(e => e.id === id ? { ...e, denomination: serviceTemp } : e));
      setEditingService(null);
    } catch (err) { alert(err.message); }
  };

  const exportCSV = () => {
    const token = sessionStorage.getItem('qbdp_token');
    fetch('https://qbdp-backend-production.up.railway.app/api/export/employes', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `employes_${new Date().toISOString().slice(0,10)}.csv`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const inviterEmploye = async (id) => {
    try {
      await api.request('/invitations/envoyer', { method: 'POST', body: JSON.stringify({ employe_id: id }) });
      showToast('Invitation envoyée !');
    } catch (err) { showToast(err.message, 'error'); }
  };

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--gray)', fontSize: 13 }}>Chargement...</div>;

  const thStyle = (col) => ({ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: sortCol === col ? 'var(--blue)' : 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' });

  return (
    <div style={{ padding: '28px 32px' }}>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          display: 'flex', alignItems: 'center', gap: 8,
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.type === 'error' ? '✕' : '✓'} {toast.msg}
        </div>
      )}

      {showModal        && <Modal onClose={() => setShowModal(false)}        onSave={handleAdd}  onGererAcces={onGererAcces} />}
      {editEmploye      && <Modal onClose={() => setEditEmploye(null)}       onSave={handleEdit} initial={editEmploye} onGererAcces={onGererAcces} />}
      {showImport       && <ImportCSV type="employes" onClose={() => setShowImport(false)} onSuccess={() => { load(); setShowImport(false); }} />}
      {absencesEmploye  && <AbsencesModal employe={absencesEmploye} onClose={() => { setAbsencesEmploye(null); load(); }} />}
      {modalEnvoi       && <ModalEnvoi selected={selected} defaultType={modalEnvoi} onClose={() => setModalEnvoi(null)} onSent={() => showToast('Message envoyé !')} />}
      {confirmModal}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>Employés</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>{allEmployees.length} employés enregistrés</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowImport(true)}
            style={{ background: 'white', color: '#1a56db', border: '2px solid #bfdbfe', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 170 }}>
            <Download size={14} /> Importer CSV
          </button>
          <button onClick={exportCSV}
            style={{ background: 'white', color: '#16a34a', border: '2px solid #bbf7d0', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 170 }}>
            <Download size={14} /> Exporter CSV
          </button>
          <button onClick={() => setShowModal(true)}
            style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 170, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            + Ajouter un employé
          </button>
        </div>
      </div>

      <div style={card}>
        <input
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', fontSize: 13, width: 280, outline: 'none', marginBottom: 16 }}
          placeholder="Rechercher…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <SelectionBar
          selected={selected}
          onClear={() => setSelected([])}
          onSendEmail={() => setModalEnvoi('email')}
          onSendSms={() => setModalEnvoi('sms')}
          onSendBoth={() => setModalEnvoi('both')}
        />
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: '#fafafa' }}>
              <th style={{ width: 36, padding: '10px 14px' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
              <th style={thStyle('nom')} onClick={() => handleSort('nom')}>Employé{arrow('nom')}</th>
              <th style={thStyle('service')} onClick={() => handleSort('service')}>Service{arrow('service')}</th>
              <th style={thStyle('statut')} onClick={() => handleSort('statut')}>Statut{arrow('statut')}</th>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, color: 'var(--gray)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</th>
              <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, color: 'var(--gray)', fontWeight: 500 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, i) => {
              const isSel = !!selected.find(s => s.id === e.id);
              return (
              <tr key={e.id} style={{ borderBottom: '1px solid var(--border)', background: isSel ? '#eff6ff' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '10px 14px' }}>
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelect(e)} style={{ cursor: 'pointer' }} />
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{e.initiales}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{e.nom}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray)' }}>ID {e.matricule || '#'+e.id}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {editingService === e.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input value={serviceTemp} onChange={ev => setServiceTemp(ev.target.value)} onKeyDown={ev => { if (ev.key === 'Enter') handleServiceSave(e.id); if (ev.key === 'Escape') setEditingService(null); }} autoFocus style={{ border: '1.5px solid #1a56db', borderRadius: 6, padding: '3px 8px', fontSize: 12, outline: 'none', width: 120 }} />
                      <button onClick={() => handleServiceSave(e.id)} style={{ background: '#1a56db', color: 'white', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>OK</button>
                      <button onClick={() => setEditingService(null)} style={{ background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={11} /></button>
                    </div>
                  ) : (
                    <div onClick={() => handleServiceEdit(e)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f9fafb', border: '0.5px solid #e5e7eb', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                      <span style={{ fontSize: 12, color: '#374151' }}>{e.denomination || e.service || '—'}</span>
                      <Pencil size={10} color="#9ca3af" />
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 14px' }}><StatusBadge statut={e.statut} enConge={e.en_conge} /></td>
                <td style={{ padding: '10px 14px', fontSize: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {e.email    && <span style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={11} color="#6b7280" />{e.email}</span>}
                    {e.telephone && <span style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={11} color="#6b7280" />{e.telephone}</span>}
                    {!e.email && !e.telephone && <span style={{ color: 'var(--gray)' }}>—</span>}
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setEditEmploye(e)} title="Modifier"
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={13} color="#374151" /></button>
                    <button onClick={() => onGererAcces(e)} title="Gérer les accès"
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Lock size={13} color="#1a56db" /></button>
                    <button onClick={() => inviterEmploye(e.id)} title="Envoyer invitation"
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Send size={13} color="#1a56db" /></button>
                    <button onClick={() => toggleStatut(e)} title={e.statut === 'actif' ? 'Suspendre' : 'Activer'}
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      {e.statut === 'actif' ? <Pause size={13} color="#374151" /> : <Play size={13} color="#374151" />}
                    </button>
                    <button onClick={() => setAbsencesEmploye(e)} title="Gérer les absences"
                      style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${e.en_conge ? '#fde68a' : 'var(--border)'}`, background: e.en_conge ? '#fef3c7' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><CalendarOff size={13} color={e.en_conge ? '#d97706' : '#374151'} /></button>
                    <button onClick={() => handleDelete(e.id, e.nom)} title="Supprimer"
                      style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #fee2e2', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={13} color="#dc2626" /></button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        {sorted.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: 'var(--gray)', fontSize: 13 }}>Aucun employé trouvé</div>}
      </div>
    </div>
  );
}
